import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

/** Ch 27.1 — HVAC Layout: zones + equipment + cooling-load summary. */
export default function HVACLayoutPanel({ projectId }) {
  const [model, setModel] = useState(null);
  const [palette, setPalette] = useState(null);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState("");

  const reload = useCallback(async () => {
    try {
      const [m, p, s] = await Promise.all([
        api.get(`/projects/${projectId}/mep-re/model`),
        api.get(`/projects/${projectId}/mep-re/palette`),
        api.get(`/projects/${projectId}/mep-re/hvac/load-summary`),
      ]);
      setModel(m.data.model);
      setPalette(p.data);
      setSummary(s.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const addZone = async () => {
    const n = (model?.hvac_zones?.length || 0) + 1;
    try {
      await api.post(`/projects/${projectId}/mep-re/hvac/zones`,
        { name: `Zone ${n}`, level: "GF", floor_area_m2: 100, occupants: 10,
          walls: [{ area_m2: 30, U: 2.2, CLTD: 15 }],
          glass: [{ area_m2: 6, SHGC: 0.7, solar_Wm2: 300, U: 5.8, dT: 12 }],
          roof_area_m2: 100, roof_U: 3.5, roof_CLTD: 25,
          LPD_Wm2: 10, EPD_Wm2: 10 });
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const delZone = async (zid) => {
    await api.delete(`/projects/${projectId}/mep-re/hvac/zones/${zid}`);
    reload();
  };
  const addEquip = async (type) => {
    const cap = palette?.hvac_equipment?.find(t => t.id === type)?.max_TR || 1.5;
    try {
      await api.post(`/projects/${projectId}/mep-re/hvac/equipment`,
        { type, level: "GF", capacity_TR: Math.min(cap, 5) });
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const delEquip = async (eid) => {
    await api.delete(`/projects/${projectId}/mep-re/hvac/equipment/${eid}`);
    reload();
  };

  return (
    <div data-testid="hvac-layout" style={{ display: "grid",
            gridTemplateColumns: "260px 1fr", gap: 12, padding: 16,
            height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* Equipment palette */}
      <div style={{ ...card, padding: 12, overflowY: "auto" }}>
        <div style={label}>HVAC Equipment</div>
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          {(palette?.hvac_equipment || []).map(t => (
            <button key={t.id} data-testid={`hvac-add-${t.id}`}
                style={{ ...btnGhost, textAlign: "left", padding: "6px 8px" }}
                onClick={() => addEquip(t.id)}>
              + {t.label}
              <span style={{ float: "right", fontSize: 10, color: "var(--pf-muted)" }}>
                ≤ {t.max_TR ? `${t.max_TR} TR` : `${t.max_cfm} cfm`}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Zones + summary */}
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr auto", gap: 12, minHeight: 0 }}>
        {/* Totals */}
        {summary?.totals && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
            <Stat label="Zones" value={summary.totals.zones} />
            <Stat label="Cooling Load" value={`${summary.totals.load_TR} TR`} />
            <Stat label="Equip count" value={summary.totals.equipment_count} />
            <Stat label="Installed" value={`${summary.totals.installed_TR} TR`} />
            <Stat label="Status" value={summary.totals.status}
                color={summary.totals.status === "OK" ? "#22c55e" : "#ef4444"} />
          </div>
        )}
        {/* Zones table */}
        <div style={{ ...card, padding: 12, overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={label}>Thermal Zones</div>
            <button data-testid="hvac-add-zone" style={btn} onClick={addZone}>+ Add Zone</button>
          </div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 11 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
              {["Name","Level","Area m²","Occ","Load TR","Sensible W","SHR","System",""]
                .map(h => <th key={h} style={{ textAlign: "left", padding: 4, color: "var(--pf-muted)" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(model?.hvac_zones || []).map(z => {
                const r = summary?.zones?.find(s => s.zone_id === z.id) || {};
                return (
                  <tr key={z.id} data-testid={`hvac-zone-${z.id}`}>
                    <td style={{ padding: 4 }}>{z.name}</td>
                    <td style={{ padding: 4 }}>{z.level}</td>
                    <td style={{ padding: 4 }}>{z.floor_area_m2}</td>
                    <td style={{ padding: 4 }}>{z.occupants}</td>
                    <td style={{ padding: 4, fontWeight: 600 }}>{r.total_TR ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.sensible_W ?? "—"}</td>
                    <td style={{ padding: 4 }}>{r.SHR ?? "—"}</td>
                    <td style={{ padding: 4, fontSize: 10 }}>{r.recommended_system ?? "—"}</td>
                    <td><button style={{ ...btnGhost, padding: "2px 6px", fontSize: 10 }}
                      onClick={() => delZone(z.id)}>×</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Equipment table */}
        <div style={{ ...card, padding: 12, maxHeight: 240, overflowY: "auto" }}>
          <div style={label}>Installed Equipment</div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 11 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
              {["Tag","Type","Level","Capacity TR",""].map(h =>
                <th key={h} style={{ textAlign: "left", padding: 4, color: "var(--pf-muted)" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(model?.hvac_equipment || []).map(e => (
                <tr key={e.id}>
                  <td style={{ padding: 4 }}>{e.tag || e.id}</td>
                  <td style={{ padding: 4 }}>{e.type}</td>
                  <td style={{ padding: 4 }}>{e.level}</td>
                  <td style={{ padding: 4 }}>{e.capacity_TR}</td>
                  <td><button style={{ ...btnGhost, padding: "2px 6px", fontSize: 10 }}
                    onClick={() => delEquip(e.id)}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {err && <div style={{ position: "absolute", bottom: 8, color: "#FF8888" }}>{err}</div>}
    </div>
  );
}

const Stat = ({ label: l, value, color }) => (
  <div style={{ ...card, padding: 8 }}>
    <div style={{ ...label, fontSize: 8 }}>{l}</div>
    <div style={{ fontSize: 16, fontWeight: 600, color: color || "var(--pf-text)", marginTop: 4 }}>{value}</div>
  </div>
);
